'use client';

import { useSeparator } from '@react-aria/separator';

import { type VariantProps } from 'tailwind-variants';

import { filterProps } from '../../internal/utils/filterProps';
import {
  type PolymorphicComponent,
  type PolymorphicComponentProps,
} from '../../types/component';
import { mergeProps } from '../../utils/mergeProps';
import { tv } from '../../utils/tv';

type Variant = VariantProps<typeof dividerStyles>;

const DEFAULT_ELEMENT_TYPE = 'div';
type DefaultElementType = typeof DEFAULT_ELEMENT_TYPE;

interface DividerBaseProps {
  /**
   * The orientation of the `Divider`.
   * @default 'horizontal'
   */
  orientation?: Variant['orientation'];
  /**
   * When `true`, the `Divider` is purely visual, carries no semantic meaning, and is hidden from the accessibility tree.
   * @default true
   */
  isDecorative?: boolean;
  /**
   * @deprecated Use `isDecorative` instead.
   *
   * @example
   * // Before
   * <Divider decorative={false} />
   *
   * // After
   * <Divider isDecorative={false} />
   */
  decorative?: boolean;
}

const dividerClasses = { root: 'zds-divider-root' } as const;

const dividerStyles = tv({
  base: [dividerClasses.root, 'shrink-0 z--divider-background-color'],
  variants: {
    orientation: {
      horizontal: 'h-[1px] w-full',
      vertical: 'h-auto min-h-full w-[1px] self-stretch',
    },
  },
  defaultVariants: { orientation: 'horizontal' },
});

type DividerProps<
  RootComponent extends React.ElementType = DefaultElementType,
> = PolymorphicComponentProps<DividerBaseProps, RootComponent>;

type DividerComponent = PolymorphicComponent<
  DividerBaseProps,
  DefaultElementType
>;

const Divider: DividerComponent = (props) => {
  const [localProps, separatorRaProps] = filterProps(
    props as DividerProps<DefaultElementType>,
    ['useSeparator']
  );

  const {
    component: Root = DEFAULT_ELEMENT_TYPE,
    className,
    isDecorative,
    decorative,
    ref,
    ...restLocalProps
  } = localProps;

  const { orientation = 'horizontal' } = props;

  const resolvedIsDecorative = isDecorative ?? decorative ?? true;

  const { separatorProps } = useSeparator({
    ...separatorRaProps,
    orientation,
    elementType: typeof Root === 'string' ? Root : DEFAULT_ELEMENT_TYPE,
  });

  const ariaProps = resolvedIsDecorative
    ? { role: 'none' as const }
    : separatorProps;

  return (
    <Root
      {...mergeProps(restLocalProps, ariaProps)}
      data-orientation={orientation}
      ref={ref}
      className={dividerStyles({ orientation, className })}
    />
  );
};

Divider.displayName = 'Divider';

export { Divider, type DividerProps, type DividerBaseProps, dividerClasses };
